import React, { useState, useCallback } from 'react';
import { Search, Building, MapPin, CheckCircle, AlertTriangle, Loader2, Shield } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CompanySearch = ({ token, onVerified }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [manualUid, setManualUid] = useState('');

  const searchCompanies = useCallback(async () => {
    if (query.length < 2) return;
    setSearching(true);
    setResults([]);
    try {
      const res = await fetch(`${API_URL}/api/company/search?q=${encodeURIComponent(query)}`);
      if (res.ok) {
        setResults(await res.json());
      }
    } catch (_) {}
    setSearching(false);
  }, [query]);

  const verifyCompany = async (uid) => {
    if (!uid || !token) return;
    setVerifying(true);
    setVerifyResult(null);
    try {
      const res = await fetch(`${API_URL}/api/company/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ uid }),
      });
      if (res.ok) {
        const data = await res.json();
        setVerifyResult(data);
        if (onVerified) onVerified(data);
      } else {
        const err = await res.json();
        setVerifyResult({ error: err.detail || 'Fehler bei der Verifizierung' });
      }
    } catch (_) {
      setVerifyResult({ error: 'Netzwerkfehler' });
    }
    setVerifying(false);
  };

  return (
    <div className="space-y-4" data-testid="company-search">
      {/* Search */}
      <div>
        <label className="text-sm font-medium block mb-1.5">Firma im Handelsregister suchen</label>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Firmenname oder UID eingeben..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && searchCompanies()}
              className="pl-9"
              data-testid="company-search-input"
            />
          </div>
          <Button onClick={searchCompanies} disabled={searching || query.length < 2} data-testid="company-search-btn">
            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Suchen'}
          </Button>
        </div>
      </div>

      {/* Results */}
      {results.length > 0 && (
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {results.map((r, i) => (
            <Card key={i} className="cursor-pointer hover:border-[hsl(var(--primary))] transition-colors"
              onClick={() => verifyCompany(r.uid)} data-testid={`company-result-${i}`}>
              <CardContent className="p-3 flex items-center justify-between">
                <div>
                  <p className="font-medium text-sm">{r.name}</p>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="text-xs text-gray-500 flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {r.legalSeat}
                    </span>
                    <span className="text-xs text-gray-500">{r.uid}</span>
                    {r.legalForm && <span className="text-xs text-gray-400">{r.legalForm}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {r.is_zo && (
                    <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">
                      ZO-Firma
                    </Badge>
                  )}
                  <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`select-company-${i}`}>
                    Auswählen
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {searching && results.length === 0 && (
        <div className="text-center py-4 text-gray-400 text-sm">
          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
          Suche im Handelsregister...
        </div>
      )}

      {/* Manual UID */}
      <div className="border-t pt-4">
        <label className="text-sm font-medium block mb-1.5">Oder UID direkt eingeben</label>
        <div className="flex gap-2">
          <Input
            placeholder="CHE-123.456.789"
            value={manualUid}
            onChange={(e) => setManualUid(e.target.value)}
            className="flex-1"
            data-testid="manual-uid-input"
          />
          <Button onClick={() => verifyCompany(manualUid)} disabled={verifying || !manualUid}
            variant="outline" data-testid="manual-uid-btn">
            {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verifizieren'}
          </Button>
        </div>
      </div>

      {/* Verification Result */}
      {verifyResult && !verifyResult.error && (
        <Alert className={verifyResult.is_zo || verifyResult.is_verein ? 'bg-emerald-50 border-emerald-200' : 'bg-blue-50 border-blue-200'}>
          {verifyResult.is_zo || verifyResult.is_verein ? (
            <CheckCircle className="h-4 w-4 text-emerald-600" />
          ) : (
            <Building className="h-4 w-4 text-blue-600" />
          )}
          <AlertDescription>
            <strong>{verifyResult.company?.name}</strong>
            <br />
            <span className="text-sm">{verifyResult.message}</span>
            {verifyResult.needs_approval && (
              <span className="block text-xs mt-1 text-gray-500">
                Die Freischaltung wird vom Admin geprüft.
              </span>
            )}
          </AlertDescription>
        </Alert>
      )}

      {verifyResult?.error && (
        <Alert className="bg-red-50 border-red-200">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-700">{verifyResult.error}</AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default CompanySearch;
